import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ProgrammingStructure } from "@models/ISchema";
import { useProgrammingStructuresPermissions } from "@modules/programming-structures/hooks/useProgrammingStructuresPermissions";
import GroupActionButton from "@modules/vacancies/Groups/group-action-button";

interface ProgrammingStructuresMenuProps {
  structure: ProgrammingStructure;
  workspace: string;
  scenario: string;
  origin: string;
  onShowPublishModal: (structure: ProgrammingStructure) => void;
  onShowDeleteModal: (structure: ProgrammingStructure) => void;
  onShowVisibilityModal: (structure: ProgrammingStructure) => void;
}

const ProgrammingStructuresMenu = ({
  structure,
  workspace,
  scenario,
  origin,
  onShowPublishModal,
  onShowDeleteModal,
  onShowVisibilityModal,
}: ProgrammingStructuresMenuProps) => {
  const history = useHistory();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { permissions } = useProgrammingStructuresPermissions();
  const isPublished = !!structure?.programmingStructureStats?.isPublished;

  const items = useMemo(() => {
    const items = [
      {
        label:
          isPublished || !permissions?.update
            ? t("programming-structures.actions.view-structure")
            : t("programming-structures.actions.edit-structure"),
        icon: (isPublished ? "external-link" : "edit") as any,
        onClick: () =>
          history.push(
            `/programming-structures/${
              isPublished || !permissions?.update ? "details" : "update"
            }/${workspace}/${scenario}/${origin}/${structure?.id}`,
          ),
      },
    ];

    if (permissions?.create) {
      items.push({
        label: t("programming-structures.actions.copy-structure"),
        icon: "copy",
        onClick: () =>
          history.push(
            `/programming-structures/new/${workspace}/${scenario}/${origin}?copyFrom=${structure?.id}`,
          ),
      });
    }

    if (!isPublished) {
      // Validate if the structure can be published.
      // It should have one stage of type module and one of type challenge
      let hasStageModule = false;
      let hasStageChallenge = false;

      structure?.programmingStructureParts?.forEach(part => {
        const stageTypeCode = part?.programmingStructureStage?.programmingStructureStageType?.code?.toUpperCase();

        if (stageTypeCode === "MODULE" || stageTypeCode === "MODULO") {
          hasStageModule = true;
        }

        /**
         * The stage type "RETO" is also considered a challenge.
         * This is because in some scenarios, like the TEC, the stage type is "RETO" instead of "CHALLENGE".
         * We keep both for compatibility with all scenarios, but we should consider standardizing this in the future to avoid confusion.
         */
        if (stageTypeCode === "CHALLENGE" || stageTypeCode === "RETO") {
          hasStageChallenge = true;
        }
      });

      if (hasStageModule && hasStageChallenge && permissions?.update) {
        items.push({
          label: t("programming-structures.actions.publish-structure"),
          icon: "upload",
          onClick: () => {
            onShowPublishModal(structure);
          },
        });
      }

      if (permissions?.delete) {
        items.push({
          label: t("programming-structures.actions.delete-structure"),
          icon: "trash",
          onClick: () => {
            onShowDeleteModal(structure);
          },
        });
      }
    }

    if (isPublished && permissions?.update) {
      items.push({
        label: structure.programmingStructureStats?.isVisible
          ? t("programming-structures.actions.hide-structure")
          : t("programming-structures.actions.show-structure"),
        icon: structure.programmingStructureStats?.isVisible ? "closed-eye" : "open-eye",
        onClick: () => {
          onShowVisibilityModal(structure);
        },
      });
    }

    return items;
  }, [structure, workspace, scenario, origin, isPublished, language, permissions]);

  return <GroupActionButton menuItems={items} buttonVariant="ghost" menuVariant="compact" />;
};

export default ProgrammingStructuresMenu;
